import clsx from "clsx";
import { ChevronDown, Minus, Plus, Wand2 } from "lucide-react";
import { useRef, useState } from "react";

import type { IncidentKind, Phase } from "../../api/types";
import { designate } from "../../components/attention";
import { Motion, Presence } from "../../components/presence";
import { Select } from "../../components/Select";
import { Button, Field, IconButton, inputClass, Segmented } from "../../components/ui";
import { k, locale, t, tServer } from "../../i18n/i18n";
import { term } from "../../i18n/vocabulary";
import { useUi } from "../../state/ui";
import { usePopover } from "./usePopover";

/** « Générer MEL/MIL » (popup « MEL/MIL par tempo » de l'iPad) : temps, nombre d'incidents par temps, type et
 *  cellule ; les incidents sont classés selon la situation du dossier puis posés sur le terrain. Bouton de
 *  l'en-tête et fenêtre surgissante (coque des menus, R9) : Échap, un appui dehors ou la génération la ferment. */

const PHASES: Phase[] = ["T1", "T2", "T3"];
const KINDS: { value: IncidentKind | "all"; label: string }[] = [
  { value: "all", label: k("Tous") },
  { value: "TAC", label: k("Tactique") },
  { value: "TEC", label: k("Technique") },
  { value: "ENV", label: k("Environnement") },
];

/** Contexte de classement tel que le serveur le résume (« posture offensive · terrain boisé »), traduit élément par
 *  élément ; la mobilité contrainte garde sa valeur (décimale de la langue de l'interface). */
function contextText(context: string): string {
  return context
    .split(" · ")
    .map((part) => {
      const mobility = /^mobilité contrainte (\d+(?:,\d+)?)$/.exec(part);
      if (mobility) {
        const value = Number(mobility[1]!.replace(",", "."));
        const shown = value.toLocaleString(locale(), { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        return t("mobilité contrainte {value}", { value: shown });
      }
      const bridges = /^(\d+) pont\(s\) détruit\(s\)$/.exec(part);
      if (bridges) return t("{n} pont(s) détruit(s)", { n: bridges[1] });
      return tServer(part);
    })
    .join(" · ");
}

export function GenerateMenu({
  open,
  onOpenChange,
  compact,
  highlight,
  cellules,
  context,
  busy,
  onGenerate,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Bouton réduit à son icône (en-tête étroit). */
  compact: boolean;
  /** Action attendue : aucun incident dans la DIREX. */
  highlight: boolean;
  cellules: string[];
  context: string;
  busy: boolean;
  onGenerate: (body: { tempos: Phase[]; count: number; type: IncidentKind | null; cellule: string | null }) => void;
}) {
  const box = useRef<HTMLDivElement>(null);
  usePopover(open, () => onOpenChange(false), box);
  return (
    <div className="relative" ref={box}>
      <Button
        size="sm"
        variant="secondary"
        onClick={() => onOpenChange(!open)}
        disabled={busy}
        title={t("Générer MEL/MIL")}
        aria-label={t("Générer MEL/MIL")}
        aria-haspopup="dialog"
        aria-expanded={open}
        highlight={highlight && !open}
      >
        <Wand2 size={14} /> {!compact && t("Générer MEL/MIL")} <ChevronDown size={14} />
      </Button>
      <Presence show={open}>
        {open && <GenerateForm cellules={cellules} context={context} busy={busy} onGenerate={onGenerate} />}
      </Presence>
    </div>
  );
}

function GenerateForm({
  cellules,
  context,
  busy,
  onGenerate,
}: {
  cellules: string[];
  context: string;
  busy: boolean;
  onGenerate: (body: { tempos: Phase[]; count: number; type: IncidentKind | null; cellule: string | null }) => void;
}) {
  const notify = useUi((s) => s.notify);
  const [tempos, setTempos] = useState<Phase[]>(PHASES);
  const [count, setCount] = useState(5);
  const [kind, setKind] = useState<IncidentKind | "all">("all");
  const [cellule, setCellule] = useState("");
  const temposRef = useRef<HTMLDivElement>(null);
  const toggle = (p: Phase) => setTempos((t) => (t.includes(p) ? t.filter((x) => x !== p) : PHASES.filter((x) => x === p || t.includes(x))));
  return (
    <Motion
      origin="top"
      role="dialog"
      aria-label={t("Générer MEL/MIL")}
      className="absolute top-full right-0 z-30 mt-1 w-[340px] max-w-[calc(100vw-16px)] space-y-3.5 rounded-xl border border-line bg-surface p-4 shadow-float"
    >
      <div role="group" aria-label={t("Temps")}>
        <span className="mb-1 block text-ui font-medium text-ink-soft">{t("Temps")}</span>
        <div ref={temposRef} className="flex gap-1.5 rounded-lg">
          {PHASES.map((p) => (
            <button
              key={p}
              type="button"
              aria-pressed={tempos.includes(p)}
              onClick={() => toggle(p)}
              className={clsx(
                "h-8 flex-1 rounded-lg border font-mono text-ui font-semibold transition-colors",
                tempos.includes(p) ? "border-accent bg-accent-soft text-accent" : "border-line-strong text-ink-muted hover:bg-hover",
              )}
            >
              {p}
            </button>
          ))}
        </div>
      </div>
      <div role="group" aria-label={t("Incidents par temps")}>
        <span className="mb-1 block text-ui font-medium text-ink-soft">{t("Incidents par temps")}</span>
        <div className="flex items-center gap-2">
          <IconButton variant="outline" onClick={() => setCount((c) => Math.max(1, c - 1))} disabled={count <= 1}
            title={t("Moins d'incidents")} aria-label={t("Moins d'incidents")}>
            <Minus size={14} />
          </IconButton>
          <span className="w-8 text-center text-title font-semibold text-ink tabular-nums">{count}</span>
          <IconButton variant="outline" onClick={() => setCount((c) => Math.min(20, c + 1))} disabled={count >= 20}
            title={t("Plus d'incidents")} aria-label={t("Plus d'incidents")}>
            <Plus size={14} />
          </IconButton>
        </div>
      </div>
      <Segmented label={t("Type")} options={KINDS.map((o) => ({ ...o, label: t(o.label) }))} value={kind} onChange={setKind} />
      <Field label={t("Cellule")}>
        <Select className={inputClass} value={cellule} onChange={(e) => setCellule(e.target.value)}>
          <option value="">{t("Toutes")}</option>
          {cellules.map((c) => (
            <option key={c} value={c}>{term(c)}</option>
          ))}
        </Select>
      </Field>
      {context && (
        <p className="text-meta leading-snug text-ink-muted">
          {t("Classés selon la situation : {context}.", { context: contextText(context) })}
        </p>
      )}
      <p className="text-meta leading-snug text-ink-muted">
        {t("Remplace les incidents générés encore prévus des temps choisis ; les incidents injectés, traités ou créés à la main restent.")}
      </p>
      <Button
        className="w-full"
        disabled={busy}
        blocked={tempos.length === 0}
        onBlocked={() => {
          // Aucun temps choisi : les temps sont l'action attendue (R12).
          designate(temposRef.current, "ring");
          notify({ text: t("Choisissez au moins un temps."), tone: "info" });
        }}
        onClick={() => onGenerate({ tempos, count, type: kind === "all" ? null : kind, cellule: cellule || null })}
      >
        <Wand2 size={14} /> {t("Générer et poser")}
      </Button>
    </Motion>
  );
}
